import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { FileText, LogOut, Search, Trash2, UserPlus, Users } from "lucide-react";
import { API, authHeaders, formatApiError, setToken } from "@/lib/api";
import { STATUS_STYLES } from "@/lib/labels";
import { Field } from "@/components/onboarding/Field";

const TABS = [
  ["all", "All"],
  ["new", "New"],
  ["reviewed", "Reviewed"],
  ["archived", "Archived"],
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [subs, setSubs] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [me, setMe] = useState(null);
  const [admins, setAdmins] = useState(null);
  const [showAdmins, setShowAdmins] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ name: "", email: "", password: "" });
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [adminError, setAdminError] = useState("");

  const loadAdmins = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers: authHeaders(), withCredentials: true });
      setAdmins(res.data);
    } catch {}
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { headers: authHeaders(), withCredentials: true });
        setMe(res.data);
      } catch {}
      loadAdmins();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addAdmin = async (e) => {
    e.preventDefault();
    setAdminError("");
    setAddingAdmin(true);
    try {
      await axios.post(
        `${API}/admin/users`,
        { name: newAdmin.name, email: newAdmin.email, password: newAdmin.password || null },
        { headers: authHeaders(), withCredentials: true }
      );
      toast.success(`Admin access granted to ${newAdmin.email}`);
      setNewAdmin({ name: "", email: "", password: "" });
      loadAdmins();
    } catch (err) {
      setAdminError(formatApiError(err));
    } finally {
      setAddingAdmin(false);
    }
  };

  const removeAdmin = async (admin) => {
    if (!window.confirm(`Remove admin access for ${admin.email}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${admin.id}`, { headers: authHeaders(), withCredentials: true });
      toast.success("Admin removed");
      loadAdmins();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/admin/submissions`, {
          params: { status, q: q || undefined },
          headers: authHeaders(),
          withCredentials: true,
        });
        setSubs(res.data);
      } catch (err) {
        if (err?.response?.status === 401) {
          setToken(null);
          navigate("/admin/login");
        } else {
          toast.error(formatApiError(err));
          setSubs([]);
        }
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [status, q, navigate]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: authHeaders(), withCredentials: true });
    } catch {}
    setToken(null);
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#08090E]">
      <header className="border-b border-white/10 bg-slate-950/70 backdrop-blur-xl sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div>
            <p className="xy-eyebrow">Xyion HQ</p>
            <h1 className="font-display text-xl sm:text-2xl font-bold tracking-tight" data-testid="admin-dashboard-title">
              Client Roster
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="btn-manage-admins"
              onClick={() => setShowAdmins((s) => !s)}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-slate-300 hover:border-violet-500/50 hover:text-violet-300 transition-colors"
            >
              <Users size={14} /> Team Access
            </button>
            <button
              data-testid="btn-admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-slate-300 hover:border-red-400/40 hover:text-red-300 transition-colors"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {showAdmins && (
          <div className="xy-card p-6 mb-6" data-testid="admin-access-panel">
            <p className="font-display font-semibold text-slate-100 mb-1">Team Access</p>
            <p className="text-xs text-slate-500 mb-5">
              Anyone listed here can sign in with the Google account matching their email — or with a password if
              you set one for them.
            </p>
            {admins === null ? (
              <p className="text-sm text-slate-500 mb-6">Loading...</p>
            ) : (
              <div className="space-y-2 mb-6">
                {admins.map((a) => (
                  <div
                    key={a.id}
                    className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3"
                    data-testid={`row-admin-${a.id}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-slate-200 truncate">{a.name || a.email}</p>
                      <p className="text-xs text-slate-500 truncate">{a.email}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="rounded-full border border-white/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-slate-400">
                        Google
                      </span>
                      {a.has_password && (
                        <span className="rounded-full border border-white/10 px-2.5 py-1 text-[10px] font-mono uppercase tracking-wider text-slate-400">
                          Password
                        </span>
                      )}
                      {me && a.id !== me.id ? (
                        <button
                          data-testid={`btn-remove-admin-${a.id}`}
                          onClick={() => removeAdmin(a)}
                          className="text-slate-500 hover:text-red-400 transition-colors"
                          aria-label={`Remove ${a.email}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      ) : (
                        <span className="text-[10px] font-mono uppercase tracking-wider text-violet-300 px-2">You</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <form onSubmit={addAdmin} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="form-add-admin">
              <Field
                label="Name"
                testid="input-new-admin-name"
                value={newAdmin.name}
                onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })}
              />
              <Field
                label="Email *"
                testid="input-new-admin-email"
                type="email"
                required
                value={newAdmin.email}
                onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })}
              />
              <Field
                label="Password (optional)"
                testid="input-new-admin-password"
                type="password"
                hint="Blank = Google sign-in only"
                value={newAdmin.password}
                onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
              />
              <button
                type="submit"
                data-testid="btn-grant-admin"
                disabled={addingAdmin}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-violet-600 px-5 py-3 text-xs font-semibold text-white hover:bg-violet-500 transition-colors disabled:opacity-40"
              >
                <UserPlus size={14} /> {addingAdmin ? "Granting..." : "Grant Access"}
              </button>
            </form>
            {adminError ? (
              <p className="mt-3 text-sm text-red-400" data-testid="add-admin-error">
                {adminError}
              </p>
            ) : null}
          </div>
        )}
        <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between mb-6">
          <div className="flex gap-2" data-testid="status-tabs">
            {TABS.map(([key, label]) => (
              <button
                key={key}
                data-testid={`tab-status-${key}`}
                onClick={() => setStatus(key)}
                className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
                  status === key
                    ? "bg-violet-600 text-white shadow-[0_0_16px_rgba(139,92,246,0.35)]"
                    : "border border-white/10 text-slate-400 hover:text-slate-200 hover:border-white/25"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              data-testid="input-admin-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search artist or legal name"
              className="xy-input pl-10 sm:w-72"
            />
          </div>
        </div>

        {subs === null ? (
          <p className="text-sm text-slate-500 py-16 text-center" data-testid="admin-loading">Loading submissions...</p>
        ) : subs.length === 0 ? (
          <div className="xy-card p-14 text-center" data-testid="admin-empty-state">
            <p className="font-display text-lg font-semibold text-slate-300">No submissions yet</p>
            <p className="text-sm text-slate-500 mt-2">
              Share your onboarding link with a client and their info will appear here.
            </p>
          </div>
        ) : (
          <div className="xy-card overflow-hidden" data-testid="admin-submissions-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left">
                    <th className="px-6 py-4 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">Artist</th>
                    <th className="px-6 py-4 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">Legal Name</th>
                    <th className="px-6 py-4 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">Docs</th>
                    <th className="px-6 py-4 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">Submitted</th>
                    <th className="px-6 py-4 text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {subs.map((s) => (
                    <tr
                      key={s.id}
                      data-testid={`row-submission-${s.id}`}
                      onClick={() => navigate(`/admin/submissions/${s.id}`)}
                      className="border-b border-white/5 cursor-pointer transition-colors hover:bg-violet-500/5"
                    >
                      <td className="px-6 py-4 font-display font-semibold text-slate-100">{s.artist_name}</td>
                      <td className="px-6 py-4 text-slate-400">{s.personal?.fullLegalName || "—"}</td>
                      <td className="px-6 py-4 text-slate-400">
                        <span className="inline-flex items-center gap-1.5">
                          <FileText size={13} className="text-slate-500" />
                          {s.documents?.length || 0}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-slate-400">
                        {s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          data-testid={`badge-status-${s.id}`}
                          className={`inline-flex rounded-full border px-3 py-1 text-[11px] font-mono uppercase tracking-wider ${STATUS_STYLES[s.status] || STATUS_STYLES.new}`}
                        >
                          {s.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="mt-8 text-center">
          <Link to="/" className="text-xs text-slate-600 hover:text-violet-300 transition-colors" data-testid="link-view-form">
            View public onboarding form
          </Link>
        </p>
      </main>
    </div>
  );
}
